import React, { createContext, useContext, useMemo } from 'react';
import { useLocalization } from '../store/utils/useLocalization';

// Translation map type
type TranslationMap = Record<string, string>;

// Translation context type
interface TranslationContextType {
  locale: string;
  translation: TranslationMap;
  t: (key: string, params?: { [k: string]: string }) => string;
}

// Create the context
const TranslationContext = createContext<TranslationContextType | null>(null);

// Provider component props
interface TranslationProviderProps {
  children: React.ReactNode;
  translation?: TranslationMap; // Optional override for testing
}

// Provider component
export function TranslationProvider({ children, translation }: TranslationProviderProps) {
  // Use the existing useLocalization hook to get translation data
  const localizationData = useLocalization();

  // Use provided translation for testing, or default to localization data
  const contextValue: TranslationContextType = useMemo(
    () =>
      translation
        ? {
            locale: 'en', // Default locale for testing
            translation,
            t: (key: string) => translation[key] || key, // Simple fallback for tests
          }
        : localizationData,
    [translation, localizationData]
  );

  return <TranslationContext.Provider value={contextValue}>{children}</TranslationContext.Provider>;
}

// Hook to use translations
export function useTranslation() {
  const context = useContext(TranslationContext);

  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }

  return context;
}
